import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BxiLogo from '../../assets/logos/bxi-logo-full.png';
import DrawerComp from './DrawerComp';
import { ToastContainer } from 'react-toastify';
import useGetLoggedInUser from '../../Hooks/LoggedInUser/useGetLoggedInUser';
import { navigateToDashboard } from '../../utils/navigation';

const fallbackPages = [
  { name: "Home", link: "/", text: "/" },
  { name: "Learn Barter", link: "/learnbarter", text: "/learnbarter" },
  { name: "About", link: "/about", text: "/about" },
  { name: "B2B Platform", link: "/marketplace", text: "/marketplace" },
  { name: "Blogs", link: "/blog", text: "/blog" },
  { name: "FAQs", link: "/faq", text: "/faq" },
  { name: "Contact Us", link: "/contact-us", text: "/contact-us" },
];
const fallbackButtons = {
  loginText: 'Log In',
  loginPath: '/login',
  signupText: 'Sign Up',
  signupPath: '/createaccount',
};

const resolveAuthPath = (path, fallbackPath) => {
  const value = String(path || fallbackPath).trim();
  if (/^https?:\/\//i.test(value)) return value;
  return value.startsWith('/') ? value : `/${value}`;
};

const Navbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMatch = useMediaQuery(theme.breakpoints.down('md'));
  const currentLocation = window.location.pathname;
  const { data: loggedInUserData } = useGetLoggedInUser();

  const pages = fallbackPages;
  const buttons = fallbackButtons;
  const logoText = 'Barter Exchange of India';
  const logoUrl = BxiLogo;

  const handleLogin = () => {
    const targetLoginUrl = resolveAuthPath(buttons.loginPath, '/login');
    if (/^https?:\/\//i.test(targetLoginUrl)) {
      window.location.href = targetLoginUrl;
      return;
    }
    navigateToDashboard(targetLoginUrl);
  };

  const handleSignup = () => {
    const targetSignupUrl = resolveAuthPath(buttons.signupPath, '/createaccount');
    if (/^https?:\/\//i.test(targetSignupUrl)) {
      window.location.href = targetSignupUrl;
      return;
    }
    window.lintrk('track', { conversion_id: 29964713 });
    navigateToDashboard(targetSignupUrl);
  };

  return (
    <React.Fragment>
      <ToastContainer style={{ fontSize: '16px' }} />
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          background: '#fff',
          boxShadow: '0px 8px 16px rgba(220, 220, 220, 0.5)',
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'end',
              flexDirection: 'row',
              gap: '8px',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <img src={logoUrl} alt="BXI logo" title="bxi logo" width={45} height={41} />
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                textAlign: 'center',
                color: '#000',
                mb: '-4px',
                textTransform: 'uppercase',
              }}
            >
              {logoText}
            </Typography>
          </Box>

          {isMatch ? (
            <DrawerComp
              pages={pages}
              isLoggedIn={Boolean(loggedInUserData?.data)}
              loginText={buttons.loginText || 'Log In'}
              signupText={buttons.signupText || 'Sign Up'}
              onLogin={handleLogin}
              onSignup={handleSignup}
              onDashboardHome={() => navigateToDashboard('/')}
            />
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {pages.map((page, index) => (
                  <Button
                    key={index}
                    sx={{
                      ...NavbarText,
                      textTransform: 'none',
                      m: '0 5px',
                      p: '6px 16px',
                      minHeight: '48px',
                      position: 'relative',
                      overflow: 'hidden',
                      color:
                        currentLocation === page.text || currentLocation === page.link
                          ? 'rgba(34, 40, 49, 1)'
                          : 'rgba(25, 24, 37, 0.5)',
                      '&::after': {
                        content: '\'\'',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '3px',
                        backgroundColor:
                          currentLocation === page.text || currentLocation === page.link
                            ? 'transparent'
                            : '#375DBB',
                        transform: 'scaleX(0)',
                        transition: 'transform 0.3s ease-in-out',
                      },
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: 'rgba(34, 40, 49, 1)',
                      },
                    }}
                    onClick={() => {
                      window.lintrk('track', { conversion_id: 29964713 });
                      navigate(page.link);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disableRipple
                  >
                    {page.name}
                  </Button>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '10px',
                }}
              >
                {/* Show Home button if user is logged in, otherwise show Login/SignUp */}
                {loggedInUserData?.data ? (
                  <Box
                    onClick={() => navigateToDashboard('/')}
                    sx={{
                      ...registerText,
                      borderRadius: '10px',
                      transition:
                        'transform 0.3s ease-in-out, background-color 0.3s ease-in-out, color 0.3s ease-in-out, text-decoration 0.3s ease-in-out',
                      backgroundColor: '#445fd2',
                      color: 'White',
                      fontSize: '14px',
                      '&:hover': {
                        backgroundColor: '#eef7ff',
                        color: '#0053ad',
                        transform: 'scale(1.05)',
                      },
                      '&:active': {
                        backgroundColor: '#0053ad',
                        color: '#ffffff',
                      },
                    }}
                  >
                    Home
                  </Box>
                ) : (
                  <>
                    <Box
                      onClick={handleLogin}
                      sx={{
                        ...registerText,
                        borderRadius: '10px',
                        transition:
                          'transform 0.3s ease-in-out, background-color 0.3s ease-in-out, color 0.3s ease-in-out, text-decoration 0.3s ease-in-out',
                        backgroundColor: '#445fd2',
                        color: 'White',
                        fontSize: '14px',
                        '&:hover': {
                          backgroundColor: '#eef7ff',
                          color: '#0053ad',
                          transform: 'scale(1.05)',
                        },
                        '&:active': {
                          backgroundColor: '#0053ad',
                          color: '#ffffff',
                        },
                      }}
                    >
                      {buttons.loginText || 'Log In'}
                    </Box>
                    <Box
                      onClick={handleSignup}
                      sx={{
                        ...registerText,
                        borderRadius: '10px',
                        color: '#fff',
                        backgroundColor: 'rgba(68, 95, 210, 1)',
                        fontSize: '14px',
                        transition:
                          'transform 0.3s ease-in-out, background-color 0.3s ease-in-out, color 0.3s ease-in-out, text-decoration 0.3s ease-in-out',
                        '&:hover': {
                          backgroundColor: '#eef7ff',
                          color: '#0053ad',
                          transform: 'scale(1.05)',
                        },
                        '&:active': {
                          backgroundColor: '#0053ad',
                          color: '#ffffff',
                        },
                      }}
                    >
                      {buttons.signupText || 'Sign Up'}
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
};

export default Navbar;

const NavbarText = {
  fontFamily: "Poppins",
  fontStyle: "normal",
  fontWeight: 600,
  fontSize: { xl: "16px", lg: "14px", md: "14px", sm: "14px", xs: "14px" },
  lineHeight: "18px",
  color: "rgba(140, 139, 146, 1)",
  cursor: "pointer",
};

const registerText = {
  fontFamily: "Poppins",
  fontSize: { xl: "12px", lg: "12px", md: "10px", sm: "12px", xs: "12px" },
  fontWeight: 600,
  border: "1px solid rgba(239, 239, 239, 1)",
  borderRadius: "16px",
  color: "rgba(34, 40, 49, 1)",
  p: 1,
  cursor: 'pointer',
  width: '70px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};


